import React, { useEffect, useState, useMemo } from "react";
import { useParams, Link } from "react-router-dom";
import { toast } from "sonner";
import { Trophy, Flame, Calendar, MapPin, Share2, Copy, Check, Medal, Users, TrendingUp, Target, ShieldCheck } from "lucide-react";
import AppLayout from "../components/AppLayout";
import { api } from "../lib/api";
import { useAuth } from "../contexts/AuthContext";

/** Player passport — public, one-glance shareable profile.
 *  Accessed via /me/passport (auth user) or /u/:uid (public). */
export default function PlayerPassport({ publicView = false }) {
  const { uid: routeUid } = useParams();
  const { user } = useAuth();
  const targetUid = publicView ? routeUid : user?.id;
  const [data, setData] = useState(null);
  const [err, setErr] = useState(null);
  const [copied, setCopied] = useState(false);

  useEffect(() => {
    if (!targetUid) return;
    api.get(`/users/${targetUid}/passport`)
      .then((r) => setData(r.data))
      .catch((e) => setErr(e.response?.status === 404 ? "Player not found" : "Couldn't load passport"));
  }, [targetUid]);

  const publicUrl = useMemo(() => {
    if (!targetUid || typeof window === "undefined") return "";
    return `${window.location.origin}/u/${targetUid}`;
  }, [targetUid]);

  const share = async () => {
    if (navigator.share) {
      try { await navigator.share({ title: "My Pick2Play passport", url: publicUrl }); return; } catch { /* cancelled */ }
    }
    try { await navigator.clipboard.writeText(publicUrl); setCopied(true); setTimeout(() => setCopied(false), 1500); toast.success("Public link copied"); }
    catch { toast.error("Couldn't copy"); }
  };

  if (!targetUid && !publicView) {
    return <AppLayout><div className="pp-card p-10 text-center text-zinc-500">Log in to see your passport.</div></AppLayout>;
  }
  if (err) {
    return <AppLayout><div className="pp-card p-10 text-center text-zinc-500" data-testid="passport-error">{err}</div></AppLayout>;
  }
  if (!data) {
    return <AppLayout><div className="pp-card p-10 text-center text-zinc-500">Loading…</div></AppLayout>;
  }

  const { user: u, stats, elo_trend, recent_matches, tournament_history, upcoming, streak } = data;

  return (
    <AppLayout>
      <div className="space-y-6" data-testid="player-passport">
        {/* Header — avatar, name, sharing */}
        <div className="pp-card-elevated p-6 sm:p-8 flex items-start gap-5 flex-wrap">
          <div className="w-20 h-20 rounded-2xl bg-gradient-to-br from-[#007AFF] to-[#00E676] flex items-center justify-center text-white font-heading text-2xl font-black shrink-0">
            {u.avatar_url
              ? <img src={u.avatar_url} alt="avatar" className="w-full h-full object-cover rounded-2xl" />
              : <span>{(u.first_name || "").slice(0, 1)}{(u.last_name || "").slice(0, 1)}</span>}
          </div>
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-2 flex-wrap">
              <h1 className="font-heading text-3xl sm:text-4xl font-black" data-testid="passport-name">{u.first_name} {u.last_name}</h1>
              {u.is_verified && <span className="text-[#00E676] flex items-center gap-1 text-xs"><ShieldCheck size={12} /> Verified</span>}
              {streak?.count > 0 && (
                <span
                  data-testid="passport-streak"
                  title={`Best streak: ${streak.best || streak.count} day${(streak.best || streak.count) === 1 ? "" : "s"}`}
                  className="text-xs px-2 py-0.5 rounded-full bg-[#FF6A00]/15 text-[#FF6A00] border border-[#FF6A00]/30 flex items-center gap-1 font-semibold"
                >
                  <Flame size={11} /> {streak.count}-day streak
                </span>
              )}
            </div>
            <div className="text-zinc-400 text-sm mt-1 flex items-center gap-3 flex-wrap">
              {u.city && u.country && <span className="flex items-center gap-1"><MapPin size={12} /> {u.city}, {u.country}</span>}
              <span className="px-2 py-0.5 rounded-full bg-white/5 text-[10px] uppercase tracking-wider">{u.role}</span>
            </div>
            {u.bio && <p className="text-zinc-400 text-sm mt-3 max-w-xl">{u.bio}</p>}
          </div>
          {!publicView && (
            <div className="flex items-center gap-2">
              <button onClick={share} data-testid="passport-share" className="pp-btn-secondary px-4 py-2 rounded-xl text-sm flex items-center gap-2">
                {copied ? <Check size={14} /> : <Share2 size={14} />} {copied ? "Copied" : "Share"}
              </button>
              <button onClick={() => { navigator.clipboard.writeText(publicUrl).then(() => { setCopied(true); setTimeout(() => setCopied(false), 1500); toast.success("Link copied"); }); }}
                      data-testid="passport-copy"
                      title="Copy public link"
                      className="pp-btn-secondary px-3 py-2 rounded-xl text-sm">
                <Copy size={14} />
              </button>
            </div>
          )}
        </div>

        {/* KPI strip — ELO, matches, win rate, tournaments */}
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-3" data-testid="passport-stats">
          <Stat icon={<Trophy size={14} />} label="ELO" value={u.elo} accent="#007AFF" testId="stat-elo" />
          <Stat icon={<Flame size={14} />} label="Matches" value={stats.matches_played} testId="stat-matches" />
          <Stat icon={<Target size={14} />} label="Win rate" value={stats.win_rate != null ? `${Math.round(stats.win_rate * 100)}%` : "—"} accent="#00E676" testId="stat-winrate" />
          <Stat icon={<Medal size={14} />} label="Tournaments" value={stats.tournaments_played} accent="#FFD60A" testId="stat-tournaments" />
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* Tournament history timeline — brand-colour accented */}
          <div className="lg:col-span-2 pp-card p-6 space-y-3" data-testid="passport-tours">
            <h2 className="font-heading text-xl font-bold flex items-center gap-2"><Trophy size={16} className="text-[#FFD60A]" /> Tournament timeline</h2>
            {tournament_history.length === 0
              ? <div className="text-zinc-500 text-sm">No tournaments yet.</div>
              : tournament_history.map((t) => (
                  <Link key={t.id} to={`/tournaments/${t.id}`}
                        data-testid={`passport-tour-${t.id}`}
                        className="flex items-center gap-4 rounded-xl border border-white/5 p-3 hover:border-[#00E676]/40 transition-colors">
                    <div className="w-1 self-stretch rounded-full" style={{ background: t.brand_color || "#007AFF" }} />
                    <div className="flex-1 min-w-0">
                      <div className="font-medium truncate" style={{ color: t.brand_color || "#FFFFFF" }}>{t.title}</div>
                      <div className="text-xs text-zinc-500 mt-0.5 flex items-center gap-3 flex-wrap">
                        <span className="flex items-center gap-1"><Trophy size={11} /> {t.sport || "—"}</span>
                        <span className="flex items-center gap-1"><Calendar size={11} /> {t.start_date ? new Date(t.start_date).toLocaleDateString() : "—"}</span>
                        {t.location && <span className="flex items-center gap-1"><MapPin size={11} /> {t.location}</span>}
                      </div>
                    </div>
                    <span className="pp-chip text-[10px]">{t.status}</span>
                  </Link>
                ))}
          </div>

          {/* Right rail — recent matches + upcoming */}
          <div className="space-y-6">
            <div className="pp-card p-6 space-y-3" data-testid="passport-recent">
              <h2 className="font-heading text-xl font-bold flex items-center gap-2"><Flame size={16} /> Recent matches</h2>
              {recent_matches.length === 0
                ? <div className="text-zinc-500 text-sm">No matches yet.</div>
                : recent_matches.map((m) => (
                    <Link key={m.id} to={`/matches/${m.id}`} className="block rounded-lg border border-white/5 p-3 text-sm hover:border-[#00E676]/30">
                      <div className="flex items-center justify-between gap-2">
                        <div className="font-medium truncate">{m.title || m.sport}</div>
                        {m.status === "completed" && (
                          <span className={`text-[10px] uppercase tracking-wider px-2 py-0.5 rounded-full ${m.won ? "bg-[#00E676]/10 text-[#00E676]" : "bg-[#FF3B30]/10 text-[#FF3B30]"}`}>
                            {m.won ? "Won" : "Lost"}
                          </span>
                        )}
                      </div>
                      <div className="text-xs text-zinc-500 mt-1 flex items-center gap-2 flex-wrap">
                        <span>{m.sport}</span>
                        <span>·</span>
                        <span>{m.start ? new Date(m.start).toLocaleDateString() : "—"}</span>
                        {m.score_summary && <span className="ml-auto text-zinc-400">{m.score_summary}</span>}
                      </div>
                    </Link>
                  ))}
            </div>
            <div className="pp-card p-6 space-y-3" data-testid="passport-upcoming">
              <h2 className="font-heading text-xl font-bold flex items-center gap-2"><Calendar size={16} /> Upcoming</h2>
              {upcoming.length === 0
                ? <div className="text-zinc-500 text-sm">Nothing on the schedule.</div>
                : upcoming.map((e) => (
                    <Link key={`${e.kind}-${e.id}`} to={`/${e.kind === "tournament" ? "tournaments" : "matches"}/${e.id}`}
                          className="block rounded-lg border border-white/5 p-3 text-sm hover:border-[#00E676]/30">
                      <div className="flex items-center gap-2">
                        {e.brand_color && <span className="w-2 h-2 rounded-full shrink-0" style={{ background: e.brand_color }} />}
                        <div className="font-medium truncate">{e.title}</div>
                      </div>
                      <div className="text-xs text-zinc-500 mt-1">
                        {e.kind} · {e.start ? new Date(e.start).toLocaleString(undefined, { month: "short", day: "numeric", hour: "2-digit", minute: "2-digit" }) : "—"}
                      </div>
                    </Link>
                  ))}
            </div>
          </div>
        </div>

        {/* ELO trend — compact sparkline */}
        {elo_trend.length > 1 && (
          <div className="pp-card p-6" data-testid="passport-elo-trend">
            <div className="flex items-center justify-between">
              <h2 className="font-heading text-xl font-bold flex items-center gap-2"><TrendingUp size={16} className="text-[#007AFF]" /> ELO trend</h2>
              <span className="text-xs text-zinc-500">{elo_trend.length} most recent changes</span>
            </div>
            <EloSparkline points={elo_trend} />
          </div>
        )}
      </div>
    </AppLayout>
  );
}

function Stat({ icon, label, value, accent, testId }) {
  return (
    <div className="pp-card p-4" data-testid={testId}>
      <div className="text-[10px] uppercase tracking-wider text-zinc-500 flex items-center gap-1">{icon}{label}</div>
      <div className="font-heading text-2xl font-bold mt-1" style={{ color: accent || "#FFFFFF" }}>{value ?? "—"}</div>
    </div>
  );
}

/** Minimal SVG sparkline — normalises new_elo values across a fixed height. */
function EloSparkline({ points }) {
  const vals = points.map((p) => p.new_elo).filter((v) => typeof v === "number");
  if (vals.length < 2) return null;
  const min = Math.min(...vals);
  const max = Math.max(...vals);
  const range = max - min || 1;
  const w = 800, h = 80, pad = 4;
  const step = (w - pad * 2) / (vals.length - 1);
  const path = vals.map((v, i) => {
    const x = pad + i * step;
    const y = h - pad - ((v - min) / range) * (h - pad * 2);
    return `${i === 0 ? "M" : "L"} ${x.toFixed(1)} ${y.toFixed(1)}`;
  }).join(" ");
  return (
    <div className="mt-4">
      <svg viewBox={`0 0 ${w} ${h}`} className="w-full h-20">
        <path d={path} fill="none" stroke="#007AFF" strokeWidth="2" />
        {points.map((p, i) => {
          const x = pad + i * step;
          const y = h - pad - ((p.new_elo - min) / range) * (h - pad * 2);
          return <circle key={p.match_id || p.at || i} cx={x} cy={y} r="3" fill={p.won ? "#00E676" : "#FF3B30"} />;
        })}
      </svg>
      <div className="mt-2 flex justify-between text-[11px] text-zinc-500">
        <span>{min}</span>
        <span className="flex items-center gap-3">
          <span className="flex items-center gap-1"><span className="w-2 h-2 rounded-full bg-[#00E676]" /> Win</span>
          <span className="flex items-center gap-1"><span className="w-2 h-2 rounded-full bg-[#FF3B30]" /> Loss</span>
        </span>
        <span>{max}</span>
      </div>
    </div>
  );
}
